// src/pages/UnsubscribePage.jsx
import React, { useContext, useState, useEffect } from "react";
import { AuthContext } from "../contexts/AuthContext";
import { supabase } from "../supabaseClient";
import { FaCheck } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const UnsubscribePage = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [subscribed, setSubscribed] = useState(null);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  // If the user is not logged in, prompt them to log in
  if (!user) {
    return (
      <div className="p-8 text-center">
        <h1 className="text-2xl font-semibold mb-4">Newsletter Preferences</h1>
        <p>Please log in to update your newsletter subscription.</p>
      </div>
    );
  }

  // Fetch the current subscription status on mount
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("profiles")
        .select("newsletter_opt_in")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) {
        console.error("Error fetching subscription status:", error);
        setMessage("An error occurred. Please try again later.");
        setSubscribed(false); // Safe fallback: treat as unsubscribed on error
      } else if (data) {
        // Expect newsletter_opt_in to be true (subscribed) or false (unsubscribed)
        setSubscribed(data.newsletter_opt_in);
      }
      setLoading(false);
    };

    fetchSubscriptionStatus();
  }, [user]);

  // Handler to update the user's newsletter preference in Supabase
  const handleUnsubscribe = async () => {
    setLoading(true);
    const { error } = await supabase
      .from("profiles")
      .update({ newsletter_opt_in: false })
      .eq("user_id", user.id);
    setLoading(false);
    if (error) {
      setMessage("An error occurred. Please try again later.");
    } else {
      setSubscribed(false);
      setMessage("You have successfully unsubscribed from our newsletter.");
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="p-8 flex flex-col items-center w-full">
      <h1 className="text-2xl font-bold mb-6">Newsletter Subscription</h1>
      {subscribed === false ? (
        <div className="flex flex-col items-center">
          <FaCheck className="text-green-600" size={40} />
          <p className="mt-4 text-lg font-medium text-center">
            You are (already) unsubscribed.
          </p>
        </div>
      ) : (
        <>
          <p className="mb-6 text-center">
            If you no longer wish to receive our newsletter, please click the button below.
          </p>
          <button
            onClick={handleUnsubscribe}
            disabled={loading}
            className="px-6 py-3 rounded-full bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50 shadow-md"
          >
            {loading ? "Processing..." : "Unsubscribe"}
          </button>
        </>
      )}
      {message && subscribed !== false && (
        <p className="mt-4 text-center">{message}</p>
      )}
    </div>
  );
};

export default UnsubscribePage;
